/**
 * ExecutiveCommandCenter — Phase 21 (full replacement of Phase 19.2).
 *
 * Sections (per Phase 21 spec):
 *   0. Global date filter (All / This Week / This Month / Last 6 Months / This Year / Custom)
 *   1. 5 KPI cards — Total Pending · Revenue · Overdue · Commercials · Offboardings (hover + click drill-down)
 *   2. Employee Performance Zone (GTM Sales, GTM Leader, CX, CX Leader, BFR, QC)
 *   3. GTM Operations bar — accounts created by GTM employee
 *   4. Revenue Funnel — Token Advance per GTM (descending)
 *
 * All numbers come from /api/insights/executive/phase21 and respect the
 * selected date filter. Drill-down data is included in the same payload
 * to avoid a second round-trip.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar, RefreshCw, Loader2, TrendingUp, IndianRupee, AlertTriangle, Briefcase, ArrowDownToLine } from 'lucide-react';
import { DrillDownTable } from '../../components/DrillDownTable';

const RANGES = [
  { id: 'all', label: 'All' },
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_6_months', label: 'Last 6 Months' },
  { id: 'this_year', label: 'This Year' },
  { id: 'custom', label: 'Custom' },
];

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${v.toLocaleString('en-IN')}`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

// Phase 21.1 — Age(H) → "89 Days 2 Hours" / "12 Hours" (no decimals).
const fmtAge = (h) => {
  const total = Math.floor(Number(h || 0));
  if (total < 24) return `${total} Hours`;
  const days = Math.floor(total / 24);
  const hours = total % 24;
  return `${days} Days ${hours} Hours`;
};

// Phase 21.1 — ISO timestamp → "2026-06-01 4:57 AM" (UTC, display-only).
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  let hr = d.getUTCHours();
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  const ampm = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12; if (hr === 0) hr = 12;
  return `${yyyy}-${mm}-${dd} ${hr}:${min} ${ampm}`;
};

// Phase 21.1 — SLA Status badge (Pending=green · Late=orange · Critical=red).
const STATUS_STYLES = {
  Pending: 'bg-emerald-100 text-emerald-700',
  Late: 'bg-orange-100 text-orange-700',
  Critical: 'bg-rose-100 text-rose-700',
};
const renderStatus = (v) => {
  const s = v || 'Pending';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[s] || STATUS_STYLES.Pending}`}>{s}</span>;
};

const KPICard = ({ id, label, value, format, breakdown, onClick, accent }) => (
  <div className="group relative bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg cursor-pointer transition-shadow"
       onClick={onClick} data-testid={`p21-kpi-${id}`}>
    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</p>
    <p className={`mt-2 text-2xl font-bold ${accent || 'text-gray-800'}`}>
      {format === 'inr' ? fmtINR(value) : fmtNum(value)}
    </p>
    {breakdown && Object.keys(breakdown).length > 0 && (
      <div className="absolute z-30 hidden group-hover:block top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl p-3" data-testid={`p21-kpi-${id}-hover`}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Breakdown</p>
        {Object.entries(breakdown).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs py-0.5"><span className="text-gray-600">{k}</span><span className="font-semibold text-gray-800">{fmtNum(v)}</span></div>
        ))}
      </div>
    )}
    <p className="mt-2 text-[10px] text-gray-400 group-hover:text-gray-600">Hover for breakdown · click to open</p>
  </div>
);

const EmployeeCard = ({ row, onClick }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md cursor-pointer" onClick={onClick} data-testid={`p21-emp-${row.name}`}>
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white flex items-center justify-center text-xs font-bold">
        {(row.name || '?').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">{row.name}</p>
        {row.accounts > 0 && <p className="text-[11px] text-gray-500">{row.accounts} accounts</p>}
      </div>
    </div>
    <div className="mt-2 grid grid-cols-3 gap-1 text-center">
      <div><p className="text-[10px] text-gray-400 uppercase">Pending</p><p className="text-sm font-bold text-gray-800">{row.pending}</p></div>
      <div><p className="text-[10px] text-gray-400 uppercase">Overdue</p><p className={`text-sm font-bold ${row.overdue > 0 ? 'text-rose-600' : 'text-gray-700'}`}>{row.overdue}</p></div>
      <div><p className="text-[10px] text-gray-400 uppercase">SLA</p><p className="text-sm font-bold text-gray-700">{row.sla_pct}%</p></div>
    </div>
    {row.revenue > 0 && <p className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-emerald-700 text-right font-semibold">{fmtINR(row.revenue)}</p>}
  </div>
);

const KPI_DRILL_COLS = {
  total_pending: [
    { key: 'property', label: 'Property' },
    { key: 'department', label: 'Department' },
    { key: 'pending_reason', label: 'Pending Reason' },
    { key: 'assigned_to', label: 'Assigned To' },
    { key: 'age_hours', label: 'Age', format: (v) => fmtAge(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
  finalized_setup: [
    { key: 'account_name', label: 'Account Name' },
    { key: 'lead_owner', label: 'Lead Owner' },
    { key: 'token_advance', label: 'Token Advance', format: (v) => fmtINR(v) },
    { key: 'finalize_setup_token', label: 'Finalize Setup Token', format: (v) => fmtINR(v) },
    { key: 'closed_won_date', label: 'Closed Won Date', format: (v) => fmtDateTime(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
  revenue: [
    { key: 'account_name', label: 'Account Name' },
    { key: 'lead_owner', label: 'Lead Owner' },
    { key: 'finalize_setup_token', label: 'Finalize Setup Token', format: (v) => fmtINR(v) },
    { key: 'token_advance', label: 'Token Advance', format: (v) => fmtINR(v) },
    { key: 'closed_won_date', label: 'Closed Won Date', format: (v) => fmtDateTime(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
  overdue: [
    { key: 'property', label: 'Property' },
    { key: 'department', label: 'Department' },
    { key: 'pending_reason', label: 'Reason' },
    { key: 'assigned_to', label: 'Assigned To' },
    { key: 'age_hours', label: 'Age', format: (v) => fmtAge(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
  commercials: [
    { key: 'property', label: 'Property' },
    { key: 'lead_owner', label: 'Lead Owner' },
    { key: 'current_stage', label: 'Stage' },
    { key: 'waiting_since', label: 'Waiting Since', format: (v) => fmtDateTime(v) },
    { key: 'age_hours', label: 'Age', format: (v) => fmtAge(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
  offboardings: [
    { key: 'property', label: 'Property' },
    { key: 'stage', label: 'Stage' },
    { key: 'age_hours', label: 'Age', format: (v) => fmtAge(v) },
    { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
  ],
};
const EMP_DRILL_COLS = [
  { key: 'property', label: 'Property' },
  { key: 'pending_reason', label: 'Task', format: (v, row) => v || row.current_stage || row.stage || '—' },
  { key: 'age_hours', label: 'Age', format: (v) => fmtAge(v) },
  { key: 'status', label: 'Status', format: (v) => renderStatus(v) },
];

const ExecutiveCommandCenter = () => {
  const [rangeKey, setRangeKey] = useState('this_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [ofCards, setOfCards] = useState({});
  const [ofPerf, setOfPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState({ open: false, title: '', context: '', rows: [], employee: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const params = new URLSearchParams({ range: rangeKey });
      if (rangeKey === 'custom') {
        if (customFrom) params.set('from', customFrom);
        if (customTo) params.set('to', customTo);
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/executive/phase21?${params}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setData(await res.json());
      // Phase 5 — Onboarding Form contribution (cards + per-user performance).
      const H = token ? { Authorization: `Bearer ${token}` } : {};
      const ofq = new URLSearchParams({ range: rangeKey });
      if (rangeKey === 'custom') { if (customFrom) ofq.set('start', customFrom); if (customTo) ofq.set('end', customTo); }
      try {
        const [oi, op] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/insights?${ofq}`, { headers: H }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/performance`, { headers: H }),
        ]);
        setOfCards(oi.ok ? (await oi.json()).cards || {} : {});
        setOfPerf(op.ok ? (await op.json()).rows || [] : []);
      } catch (e) { setOfCards({}); setOfPerf([]); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [rangeKey, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const openKpiDrill = (kpiId, title) => {
    if (!data) return;
    setDrill({ open: true, title, context: kpiId, rows: (data.drill || {})[kpiId] || [] });
  };

  const openEmployeeDrill = async (row) => {
    if (!data) return;
    // Phase 21.1 — use the backend-provided per-employee drill map so the
    // drill-down rows ALWAYS match the card's pending/overdue counts.
    const own = (data.employee_drill || {})[row.name] || [];
    setDrill({ open: true, title: `${row.name} · Pending + Overdue`, context: 'employee', rows: own });
  };

  const gtmBarMax = useMemo(() => Math.max(1, ...(data?.gtm_bar || []).map((r) => r.accounts)), [data]);
  const revFunnelMax = useMemo(() => Math.max(1, ...(data?.revenue_funnel || []).map((r) => r.revenue)), [data]);

  if (loading && !data) return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={20} className="animate-spin mr-2" />Loading Phase 21…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="executive-command-center">
      {/* Section 0 — Global Date Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-3" data-testid="p21-date-filter">
        <Calendar size={16} className="text-teal-600" />
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setRangeKey(r.id)} data-testid={`p21-range-${r.id}`}
                    className={`px-3 py-1.5 text-xs rounded-full border ${rangeKey === r.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:border-teal-300'}`}>
              {r.label}
            </button>
          ))}
        </div>
        {rangeKey === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="p21-custom-from" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="p21-custom-to" />
          </div>
        )}
        <button onClick={load} className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white rounded-lg border border-gray-200 hover:border-teal-300" data-testid="p21-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Section 1 — 6 KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPICard id="total_pending" label="Total Pending Work" value={(data.kpi.total_pending.value || 0) + (ofCards.of_pending || 0)} breakdown={{ ...data.kpi.total_pending.breakdown, 'Onboarding Form': ofCards.of_pending || 0 }} onClick={() => openKpiDrill('total_pending', 'Total Pending Work')} accent="text-amber-700" />
        <KPICard id="finalized_setup" label="Finalized Setup" value={data.kpi.finalized_setup?.value || 0} format="inr" breakdown={{ 'Accounts': data.kpi.finalized_setup?.count || 0 }} onClick={() => openKpiDrill('finalized_setup', 'Finalized Setup')} accent="text-teal-700" />
        <KPICard id="revenue" label="Revenue Collected" value={data.kpi.revenue.value} format="inr" breakdown={{ 'Accounts': data.kpi.revenue.count }} onClick={() => openKpiDrill('revenue', 'Revenue · Token Advance')} accent="text-emerald-700" />
        <KPICard id="overdue" label="Overdue Tasks" value={(data.kpi.overdue.value || 0) + (ofCards.overdue || 0)} breakdown={{ ...data.kpi.overdue.breakdown, 'Onboarding Form': ofCards.overdue || 0 }} onClick={() => openKpiDrill('overdue', 'Overdue Tasks')} accent="text-rose-700" />
        <KPICard id="commercials" label="Pending Commercial Approvals" value={data.kpi.commercials.value} breakdown={data.kpi.commercials.breakdown} onClick={() => openKpiDrill('commercials', 'Pending Commercial Approvals')} accent="text-indigo-700" />
        <KPICard id="offboardings" label="Pending Offboardings" value={data.kpi.offboardings.value} breakdown={data.kpi.offboardings.breakdown} onClick={() => openKpiDrill('offboardings', 'Pending Offboardings')} accent="text-orange-700" />
      </div>

      {/* Section 2 — Employee Performance Zone */}
      <section data-testid="p21-employee-zone">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">Employee Performance Zone</h3>
        <div className="space-y-3">
          {['gtm_sales', 'gtm_sales_leader', 'cx', 'cx_leader', 'bfr', 'qc'].map((role) => {
            const rows = data.employee_zones[role] || [];
            if (rows.length === 0) return null;
            return (
              <div key={role} className="bg-white rounded-xl border border-gray-200 p-3">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">{role.replace(/_/g, ' ')} ({rows.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {rows.map((r) => <EmployeeCard key={r.name} row={r} onClick={() => openEmployeeDrill(r)} />)}
                </div>
              </div>
            );
          })}
          {/* Phase 5 — Onboarding Form section (after QC) */}
          {ofPerf.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-3" data-testid="p21-of-zone">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Onboarding Form ({ofPerf.length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {ofPerf.map((r) => (
                  <EmployeeCard key={r.user_id} row={{ name: r.name, pending: r.pending, overdue: r.overdue, sla_pct: r.sla_percent }} onClick={() => {}} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Section 3 — GTM Operations Bar Graph */}
      <section data-testid="p21-gtm-bar">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2"><TrendingUp size={14} className="text-teal-600" /> Accounts Created · GTM Employees</h3>
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          {(data.gtm_bar || []).length === 0 ? <p className="text-xs text-gray-400 py-6 text-center">No GTM activity in range.</p>
            : data.gtm_bar.map((r) => (
              <div key={r.name} className="flex items-center gap-2" data-testid={`p21-gtm-bar-${r.name}`}>
                <div className="w-32 text-xs text-gray-700 truncate">{r.name}</div>
                <div className="flex-1 relative h-5 bg-gray-100 rounded">
                  <div className="absolute inset-y-0 left-0 rounded bg-gradient-to-r from-teal-400 to-teal-600" style={{ width: `${Math.round((r.accounts / gtmBarMax) * 100)}%` }} />
                </div>
                <div className="w-12 text-right text-xs font-semibold text-gray-800">{r.accounts}</div>
              </div>
            ))}
        </div>
      </section>

      {/* Section 4 — Revenue Funnel */}
      <section data-testid="p21-revenue-funnel">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2"><IndianRupee size={14} className="text-emerald-600" /> Revenue Funnel · Token Advance</h3>
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-1.5">
          {(data.revenue_funnel || []).length === 0 ? <p className="text-xs text-gray-400 py-6 text-center">No revenue in range.</p>
            : data.revenue_funnel.map((r, i) => {
              const width = 100 - i * 8;
              return (
                <div key={r.name} className="flex items-center gap-2">
                  <div className="w-32 text-xs text-gray-700 truncate">{r.name}</div>
                  <div className={`h-7 rounded text-white text-xs font-semibold flex items-center px-3 bg-gradient-to-r ${i === 0 ? 'from-emerald-500 to-emerald-700' : 'from-emerald-400 to-emerald-500'}`}
                       style={{ width: `${Math.max(width, 30)}%` }}>
                    {fmtINR(r.revenue)}
                  </div>
                </div>
              );
            })}
        </div>
      </section>

      <DrillDownTable
        open={drill.open}
        title={drill.title}
        columns={drill.context === 'employee' ? EMP_DRILL_COLS : (KPI_DRILL_COLS[drill.context] || [])}
        rows={drill.rows}
        onClose={() => setDrill({ open: false, title: '', context: '', rows: [] })}
      />
    </div>
  );
};

export default ExecutiveCommandCenter;
